import * as THREE from 'three';
import type { BodyState } from '../astro/Universe';
import { commonGlsl } from './shaders';

// Streams real Earth imagery (Esri World Imagery) draped over real elevation (AWS Terrain Tiles,
// Terrarium encoding) as a Web-Mercator quadtree around the camera.

const IMAGERY_URL = (z: number, x: number, y: number) =>
  `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;
const ELEVATION_URL = (z: number, x: number, y: number) =>
  `https://s3.amazonaws.com/elevation-tiles-prod/terrarium/${z}/${x}/${y}.png`;

const ROOT_Z = 3;
const MAX_Z = 18;
const MAX_ELEV_Z = 15;
const GRID = 32;
const SPLIT_FACTOR = 2.2; // split when camera distance < factor × tile width
const MAX_CONCURRENT = 12;
const CACHE_LIMIT = 700;
export const TILES_MAX_ALTITUDE = 1_600_000; // m: tiles only below this
const FADE_START = 1_600_000;
const FADE_END = 1_100_000;

type Elevation = { data: Float32Array; size: number };

type Tile = {
  z: number;
  x: number;
  y: number;
  key: string;
  state: 'new' | 'loading' | 'ready' | 'error';
  mesh?: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>;
  texture?: THREE.Texture;
  elevation?: Elevation; // this tile's own or ancestor-derived
  center: THREE.Vector3; // body-fixed, metres
  width: number; // metres
  lastUsed: number;
  children?: Tile[];
};

const PI = Math.PI;
function tileLon(x: number, z: number): number {
  return (x / 2 ** z) * 360 - 180;
}
function tileLat(y: number, z: number): number {
  const n = PI - (2 * PI * y) / 2 ** z;
  return (180 / PI) * Math.atan(Math.sinh(n));
}
function mercY(latDeg: number): number {
  const lat = (latDeg * PI) / 180;
  return (1 - Math.log(Math.tan(lat) + 1 / Math.cos(lat)) / PI) / 2;
}
function toBodyFixed(latDeg: number, lonDeg: number, r: number, out: THREE.Vector3): THREE.Vector3 {
  const lat = (latDeg * PI) / 180, lon = (lonDeg * PI) / 180;
  return out.set(r * Math.cos(lat) * Math.cos(lon), r * Math.cos(lat) * Math.sin(lon), r * Math.sin(lat));
}

const tileVertex = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_vertex>
  attribute float water;
  varying vec2 vUv;
  varying vec3 vDir;       // body-fixed unit direction
  varying vec3 vWorldPos;  // render space
  varying vec3 vNormalW;
  varying vec3 vUpW;
  varying float vWater;
  uniform vec3 tileCenterDir;
  void main() {
    vUv = uv;
    vWater = water;
    vDir = normalize(position + tileCenter);
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    vNormalW = normalize(mat3(modelMatrix) * normal);
    vUpW = normalize(mat3(modelMatrix) * vDir);
    gl_Position = projectionMatrix * viewMatrix * wp;
    #include <logdepthbuf_vertex>
  }
`;

const tileFragment = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_fragment>
  uniform sampler2D map;
  uniform sampler2D nightMap;
  uniform sampler2D cloudMap;
  uniform vec3 sunPos;
  uniform float cloudShift;
  uniform float fade;
  uniform vec3 hazeColor;
  uniform float hazeDensity;
  uniform float lightsScale;
  varying vec2 vUv;
  varying vec3 vDir;
  varying vec3 vWorldPos;
  varying vec3 vNormalW;
  varying vec3 vUpW;
  varying float vWater;
  ${commonGlsl}
  float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
  void main() {
    #include <logdepthbuf_fragment>
    if (fade < 1.0 && hash(gl_FragCoord.xy) > fade) discard;
    vec3 albedo = texture2D(map, vUv).rgb;
    vec3 N = normalize(vNormalW);
    vec3 Up = normalize(vUpW);
    vec3 L = normalize(sunPos - vWorldPos);
    vec3 V = normalize(-vWorldPos);
    float ngl = dot(Up, L);
    float nl = max(dot(N, L), 0.0);
    vec3 sunColor = mix(vec3(1.0, 0.42, 0.18), vec3(1.0), smoothstep(-0.02, 0.35, ngl));
    float lit = nl * smoothstep(-0.05, 0.05, ngl);
    // Cloud shadows from the global cloud layer.
    vec3 cd = vDir;
    float c = cos(cloudShift), s = sin(cloudShift);
    cd.xy = mat2(c, s, -s, c) * cd.xy;
    float cloud = sampleSphere(cloudMap, sphereUv(cd)).r;
    float shadow = 1.0 - 0.5 * cloud;
    // Esri imagery is photographed in daylight: treat it as albedo with a gentle lift.
    vec3 color = pow(albedo, vec3(1.08)) * sunColor * lit * shadow * 1.55;
    // Sky fill light
    color += albedo * vec3(0.35, 0.45, 0.6) * 0.12 * smoothstep(-0.1, 0.3, ngl);
    if (vWater > 0.5) {
      vec3 H = normalize(L + V);
      float fres = 0.02 + 0.98 * pow(1.0 - max(dot(V, Up), 0.0), 5.0);
      float spec = pow(max(dot(Up, H), 0.0), 180.0) * 2.5;
      color += sunColor * spec * (0.3 + fres) * smoothstep(0.0, 0.1, ngl) * shadow;
    }
    // City lights: the 8K map is ~5 km per texel, so near the ground modulate it by the
    // imagery (bright built-up pixels glow, fields and forests stay dark) and fade overall.
    vec3 lights = sampleSphere(nightMap, sphereUv(vDir)).rgb;
    float luma = dot(albedo, vec3(0.299, 0.587, 0.114));
    float grey = 1.0 - clamp((max(albedo.r, max(albedo.g, albedo.b)) - min(albedo.r, min(albedo.g, albedo.b))) * 4.0, 0.0, 1.0);
    float urban = mix(1.0, smoothstep(0.18, 0.45, luma) * grey, 1.0 - lightsScale);
    color += lights * urban * vec3(1.0, 0.78, 0.5) * (1.0 - smoothstep(-0.12, 0.06, ngl)) * mix(0.35, 1.6, lightsScale);
    // Aerial perspective
    float dist = length(vWorldPos);
    float haze = 1.0 - exp(-dist * hazeDensity);
    color = mix(color, hazeColor, haze);
    gl_FragColor = vec4(color, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

export class EarthTiles {
  readonly group = new THREE.Group();
  private readonly tiles = new Map<string, Tile>();
  private readonly roots: Tile[] = [];
  private readonly elevCache = new Map<string, Promise<Elevation | null>>();
  private readonly elevReady = new Map<string, Elevation>();
  private readonly texLoader = new THREE.TextureLoader();
  private loading = 0;
  private frame = 0;
  private readonly queue: Tile[] = [];
  private readonly uniformsShared = {
    nightMap: { value: null as THREE.Texture | null },
    cloudMap: { value: null as THREE.Texture | null },
    sunPos: { value: new THREE.Vector3() },
    cloudShift: { value: 0 },
    fade: { value: 1 },
    hazeColor: { value: new THREE.Color(0.6, 0.7, 0.85) },
    hazeDensity: { value: 1 / 60000 },
    lightsScale: { value: 1 },
  };
  active = false;
  visibleTiles = 0;
  private readonly R: number;
  private readonly anisotropy: number;

  constructor(body: BodyState, renderer: THREE.WebGLRenderer) {
    this.R = body.def.radius;
    this.anisotropy = renderer.capabilities.getMaxAnisotropy();
    this.texLoader.setCrossOrigin('anonymous');
    const n = 2 ** ROOT_Z;
    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) this.roots.push(this.getTile(ROOT_Z, x, y));
    this.group.visible = false;
  }

  get pending(): number {
    return this.loading + this.queue.length;
  }

  setSharedTextures(night: THREE.Texture | null, clouds: THREE.Texture | null): void {
    this.uniformsShared.nightMap.value = night;
    this.uniformsShared.cloudMap.value = clouds;
  }

  private getTile(z: number, x: number, y: number): Tile {
    const key = `${z}/${x}/${y}`;
    let t = this.tiles.get(key);
    if (!t) {
      const latC = tileLat(y + 0.5, z), lonC = tileLon(x + 0.5, z);
      const center = toBodyFixed(latC, lonC, this.R, new THREE.Vector3());
      const width = ((2 * PI * this.R) / 2 ** z) * Math.cos((latC * PI) / 180);
      t = { z, x, y, key, state: 'new', center, width, lastUsed: 0 };
      this.tiles.set(key, t);
    }
    return t;
  }

  /** Height (m above sea level) at a body-fixed direction, from the best loaded elevation. */
  heightAt(dirBodyFixed: THREE.Vector3): number {
    const lat = (Math.asin(THREE.MathUtils.clamp(dirBodyFixed.z, -1, 1)) * 180) / PI;
    const lon = (Math.atan2(dirBodyFixed.y, dirBodyFixed.x) * 180) / PI;
    if (Math.abs(lat) > 85) return 0;
    const my = mercY(lat), mx = (lon + 180) / 360;
    for (let z = MAX_ELEV_Z; z >= 0; z--) {
      const n = 2 ** z;
      const tx = Math.floor(mx * n), ty = Math.floor(my * n);
      const e = this.elevReady.get(`${z}/${tx}/${ty}`);
      if (!e) continue;
      const u = mx * n - tx, v = my * n - ty;
      return Math.max(0, sampleElevation(e, u, v));
    }
    return 0;
  }

  update(cameraBodyFixed: THREE.Vector3, originToEarth: THREE.Vector3, earthQuat: THREE.Quaternion, sunRender: THREE.Vector3, cloudShift: number, daylight: number): void {
    this.frame++;
    const alt = cameraBodyFixed.length() - this.R;
    this.active = alt < TILES_MAX_ALTITUDE;
    this.group.visible = this.active;
    if (!this.active) {
      this.evict(true);
      return;
    }
    this.uniformsShared.fade.value = THREE.MathUtils.clamp((FADE_START - alt) / (FADE_START - FADE_END), 0, 1);
    this.uniformsShared.sunPos.value.copy(sunRender);
    this.uniformsShared.lightsScale.value = THREE.MathUtils.smoothstep(alt, 3_000, 60_000);
    this.uniformsShared.cloudShift.value = cloudShift;
    // Haze: thicker near the ground, tinted by daylight.
    this.uniformsShared.hazeDensity.value = ((1 / 45000) * Math.exp(-Math.max(0, alt) / 8000) + 1 / 900000) * 0.35;
    const day = new THREE.Color(0.62, 0.74, 0.9), dusk = new THREE.Color(0.02, 0.03, 0.06);
    this.uniformsShared.hazeColor.value.copy(dusk).lerp(day, daylight);

    // Horizon culling
    const camDist = cameraBodyFixed.length();
    const horizonAngle = Math.acos(Math.min(1, this.R / camDist));
    const camDir = cameraBodyFixed.clone().normalize();

    for (const t of this.tiles.values()) if (t.mesh) t.mesh.visible = false;
    this.visibleTiles = 0;
    const want: Tile[] = [];
    const visit = (t: Tile) => {
      t.lastUsed = this.frame;
      // Horizon test with the tile's angular radius.
      const tileAng = t.width / this.R;
      const angle = camDir.angleTo(t.center);
      if (t.z > ROOT_Z && angle > horizonAngle + tileAng * 1.2 + 0.02) return;
      const dist = cameraBodyFixed.distanceTo(t.center);
      const split = t.z < MAX_Z && dist < t.width * SPLIT_FACTOR;
      if (split) {
        const kids = this.childrenOf(t);
        const kidsReady = kids.every((k) => k.state === 'ready' || k.state === 'error');
        kids.forEach((k) => {
          k.lastUsed = this.frame;
          if (k.state === 'new') want.push(k);
        });
        if (kidsReady) {
          kids.forEach(visit);
          return;
        }
      }
      if (t.state === 'new') want.push(t);
      if (t.state === 'ready' && t.mesh) {
        this.place(t, originToEarth, earthQuat);
        t.mesh.visible = true;
        this.visibleTiles++;
      } else if (t.state !== 'ready') {
        // Parent draws nothing yet: its ancestors covered this area last frame (base globe beneath).
      }
    };
    this.roots.forEach(visit);

    // Load nearest first.
    want.sort((a, b) => a.center.distanceToSquared(cameraBodyFixed) / a.width - b.center.distanceToSquared(cameraBodyFixed) / b.width);
    for (const t of want) {
      if (this.loading >= MAX_CONCURRENT) break;
      if (t.state === 'new') this.load(t);
    }
    this.evict(false);
  }

  private childrenOf(t: Tile): Tile[] {
    if (!t.children) {
      const z = t.z + 1, x = t.x * 2, y = t.y * 2;
      t.children = [this.getTile(z, x, y), this.getTile(z, x + 1, y), this.getTile(z, x, y + 1), this.getTile(z, x + 1, y + 1)];
    }
    return t.children;
  }

  private place(t: Tile, originToEarth: THREE.Vector3, earthQuat: THREE.Quaternion): void {
    // Tile centre in render space computed in float64, then handed to the GPU as a small offset.
    const m = t.mesh!;
    m.position.copy(t.center).applyQuaternion(earthQuat).add(originToEarth);
    m.quaternion.copy(earthQuat);
  }

  private async load(t: Tile): Promise<void> {
    t.state = 'loading';
    this.loading++;
    try {
      const ez = Math.min(t.z, MAX_ELEV_Z);
      const shift = t.z - ez;
      const ex = t.x >> shift, ey = t.y >> shift;
      const [texture, elev] = await Promise.all([this.loadImagery(t), this.loadElevation(ez, ex, ey)]);
      if (t.lastUsed < this.frame - 600) {
        texture.dispose();
        t.state = 'new';
        return;
      }
      t.texture = texture;
      t.elevation = elev ?? undefined;
      t.mesh = this.buildMesh(t, elev, shift, ex, ey);
      t.mesh.visible = false;
      this.group.add(t.mesh);
      t.state = 'ready';
    } catch {
      t.state = 'error';
    } finally {
      this.loading--;
    }
  }

  private loadImagery(t: Tile): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      this.texLoader.load(
        IMAGERY_URL(t.z, t.x, t.y),
        (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          tex.anisotropy = this.anisotropy;
          tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
          tex.minFilter = THREE.LinearMipmapLinearFilter;
          resolve(tex);
        },
        undefined,
        reject,
      );
    });
  }

  private loadElevation(z: number, x: number, y: number): Promise<Elevation | null> {
    const key = `${z}/${x}/${y}`;
    let p = this.elevCache.get(key);
    if (!p) {
      p = fetch(ELEVATION_URL(z, x, y))
        .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
        .then((b) => createImageBitmap(b))
        .then((bmp) => {
          const size = bmp.width;
          const c = new OffscreenCanvas(size, size);
          const g = c.getContext('2d', { willReadFrequently: true })!;
          g.drawImage(bmp, 0, 0);
          const px = g.getImageData(0, 0, size, size).data;
          const data = new Float32Array(size * size);
          for (let i = 0; i < size * size; i++) data[i] = px[i * 4] * 256 + px[i * 4 + 1] + px[i * 4 + 2] / 256 - 32768;
          const e = { data, size };
          this.elevReady.set(key, e);
          return e;
        })
        .catch(() => null);
      this.elevCache.set(key, p);
    }
    return p;
  }

  private buildMesh(t: Tile, elev: Elevation | null, shift: number, ex: number, ey: number): THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial> {
    const N = GRID + 1;
    const vertCount = N * N + 4 * N; // grid + skirts
    const pos = new Float32Array(vertCount * 3);
    const uv = new Float32Array(vertCount * 2);
    const water = new Float32Array(vertCount);
    const tmp = new THREE.Vector3();
    const n = 2 ** t.z;
    const sub = 2 ** shift;
    const heights = new Float32Array(N * N);
    for (let j = 0; j < N; j++) {
      for (let i = 0; i < N; i++) {
        const fx = t.x + i / GRID, fy = t.y + j / GRID; // tile coords at zoom z
        const lon = (fx / n) * 360 - 180;
        const lat = tileLat(fy, t.z);
        let h = 0;
        if (elev) {
          const u = (fx / sub - ex), v = (fy / sub - ey);
          h = sampleElevation(elev, u, v);
        }
        const isWater = h <= 0.5 ? 1 : 0;
        h = Math.max(0, h);
        heights[j * N + i] = h;
        toBodyFixed(lat, lon, this.R + h, tmp).sub(t.center);
        const k = j * N + i;
        pos.set([tmp.x, tmp.y, tmp.z], k * 3);
        uv.set([i / GRID, 1 - j / GRID], k * 2);
        water[k] = isWater;
      }
    }
    // Skirts: duplicate edge vertices pushed down.
    const skirtDepth = Math.max(30, t.width * 0.03);
    const edges: number[] = [];
    for (let i = 0; i < N; i++) edges.push(i); // top
    for (let i = 0; i < N; i++) edges.push((N - 1) * N + i); // bottom
    for (let j = 0; j < N; j++) edges.push(j * N); // left
    for (let j = 0; j < N; j++) edges.push(j * N + N - 1); // right
    const up = new THREE.Vector3();
    edges.forEach((src, e) => {
      const k = N * N + e;
      tmp.fromArray(pos, src * 3).add(t.center);
      up.copy(tmp).normalize();
      tmp.addScaledVector(up, -skirtDepth).sub(t.center);
      pos.set([tmp.x, tmp.y, tmp.z], k * 3);
      uv.set([uv[src * 2], uv[src * 2 + 1]], k * 2);
      water[k] = water[src];
    });
    const index: number[] = [];
    for (let j = 0; j < GRID; j++) {
      for (let i = 0; i < GRID; i++) {
        const a = j * N + i, b = a + 1, c = a + N, d = c + 1;
        index.push(a, c, b, b, c, d);
      }
    }
    const skirt = (row: number, flip: boolean) => {
      for (let i = 0; i < GRID; i++) {
        const e0 = row * N + i, e1 = e0 + 1;
        const src0 = edges[e0], src1 = edges[e1];
        const s0 = N * N + e0, s1 = N * N + e1;
        if (flip) index.push(src0, src1, s0, s0, src1, s1);
        else index.push(src0, s0, src1, src1, s0, s1);
      }
    };
    skirt(0, false);
    skirt(1, true);
    skirt(2, true);
    skirt(3, false);

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    geo.setAttribute('water', new THREE.BufferAttribute(water, 1));
    geo.setIndex(index);
    geo.computeVertexNormals();
    geo.computeBoundingSphere();

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        ...this.uniformsShared,
        map: { value: t.texture },
        tileCenter: { value: t.center.clone() },
      },
      vertexShader: tileVertex.replace('uniform vec3 tileCenterDir;', 'uniform vec3 tileCenter;'),
      fragmentShader: tileFragment,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.renderOrder = 0;
    return mesh;
  }

  private evict(all: boolean): void {
    const ready = [...this.tiles.values()].filter((t) => t.mesh);
    if (!all && ready.length < CACHE_LIMIT) return;
    ready.sort((a, b) => a.lastUsed - b.lastUsed);
    const drop = all ? ready.length : ready.length - CACHE_LIMIT * 0.8;
    for (let i = 0; i < drop; i++) {
      const t = ready[i];
      if (!all && t.lastUsed >= this.frame - 2) break;
      this.disposeTile(t);
    }
  }

  private disposeTile(t: Tile): void {
    if (t.mesh) {
      this.group.remove(t.mesh);
      t.mesh.geometry.dispose();
      t.mesh.material.dispose();
      t.mesh = undefined;
    }
    t.texture?.dispose();
    t.texture = undefined;
    t.state = 'new';
  }
}

function sampleElevation(e: Elevation, u: number, v: number): number {
  const s = e.size;
  const fx = THREE.MathUtils.clamp(u * s - 0.5, 0, s - 1);
  const fy = THREE.MathUtils.clamp(v * s - 0.5, 0, s - 1);
  const x0 = Math.floor(fx), y0 = Math.floor(fy);
  const x1 = Math.min(s - 1, x0 + 1), y1 = Math.min(s - 1, y0 + 1);
  const tx = fx - x0, ty = fy - y0;
  const d = e.data;
  const a = d[y0 * s + x0], b = d[y0 * s + x1], c = d[y1 * s + x0], dd = d[y1 * s + x1];
  return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + dd * tx) * ty;
}
